import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import {
  Button,
  Drawer,
  FormControlLabel,
  IconButton,
  InputAdornment,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useDeliveryAreaActions } from 'src/context/providers/DeliveryAreaContext';
import { DirectionFlags } from '../orderPhysical/direction-picker-modal';
import LocationPickerMap from './location-picker-map';
import { AREA_DELIVERY_OPTIONS } from './view/data';

const EMPTY_AREA = {
  destination_delivery: AREA_DELIVERY_OPTIONS[0].value,
  name: '',
  city: '',
  description: '',
  price_usd: '',
  latitude: '',
  longitude: '',
  is_active: true,
};

const toForm = (area) =>
  area
    ? Object.fromEntries(Object.keys(EMPTY_AREA).map((key) => [key, area[key] ?? EMPTY_AREA[key]]))
    : EMPTY_AREA;

const toPayload = (form) => ({
  destination_delivery: form.destination_delivery,
  name: form.name.trim(),
  city: form.city.trim() || undefined,
  description: form.description.trim() || undefined,
  price_usd: Number(form.price_usd),
  latitude: form.latitude === '' ? undefined : Number(form.latitude),
  longitude: form.longitude === '' ? undefined : Number(form.longitude),
  is_active: form.is_active,
});

const DeliveryAreaDrawer = ({ open, area, onClose, onSaved }) => {
  const { saveDeliveryArea } = useDeliveryAreaActions();
  const [form, setForm] = useState(EMPTY_AREA);
  const [submitted, setSubmitted] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(toForm(area));
      setSubmitted(false);
    }
  }, [open, area]);

  const option = AREA_DELIVERY_OPTIONS.find((o) => o.value === form.destination_delivery);
  const missingName = !form.name.trim();
  const missingPrice = form.price_usd === '' || Number(form.price_usd) < 0;

  const setField = (name) => (event) => setForm((prev) => ({ ...prev, [name]: event.target.value }));

  const handleSave = async () => {
    setSubmitted(true);
    if (missingName || missingPrice) return;
    setSaving(true);
    const saved = await saveDeliveryArea(area?.id, toPayload(form));
    setSaving(false);
    if (saved) {
      onSaved(saved);
      onClose();
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: { xs: '100%', md: 520 } } }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ px: 2.5, py: 2, borderBottom: '1px solid', borderColor: 'divider' }}
      >
        <Typography variant="h6">{area ? 'Edit delivery area' : 'New delivery area'}</Typography>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Stack>

      <Stack spacing={2.5} sx={{ p: 2.5, flex: 1, overflowY: 'auto' }}>
        <TextField
          select
          label="Delivery type"
          value={form.destination_delivery}
          onChange={setField('destination_delivery')}
          SelectProps={{
            renderValue: (value) => {
              const selected = AREA_DELIVERY_OPTIONS.find((o) => o.value === value);
              return (
                <Stack direction="row" alignItems="center" spacing={1}>
                  <DirectionFlags direction={selected.direction} size={18} />
                  <span>{selected.label}</span>
                </Stack>
              );
            },
          }}
        >
          {AREA_DELIVERY_OPTIONS.map((o) => (
            <MenuItem key={o.value} value={o.value}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <DirectionFlags direction={o.direction} size={18} />
                <span>{o.label}</span>
              </Stack>
            </MenuItem>
          ))}
        </TextField>

        <TextField
          label="Name *"
          placeholder="Brooklyn"
          value={form.name}
          onChange={setField('name')}
          error={submitted && missingName}
          helperText={submitted && missingName ? 'Name is required' : ''}
        />
        <TextField
          label="City / state"
          placeholder={option?.cityPlaceholder}
          value={form.city}
          onChange={setField('city')}
        />
        <TextField
          label="Price per group *"
          type="number"
          value={form.price_usd}
          onChange={setField('price_usd')}
          InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
          inputProps={{ min: 0, step: '0.01' }}
          error={submitted && missingPrice}
          helperText={
            submitted && missingPrice
              ? 'Price is required'
              : 'Charged once per group that picks this area'
          }
        />
        <TextField
          label="Description"
          placeholder="ZIP codes or neighbourhoods covered"
          value={form.description}
          onChange={setField('description')}
          multiline
          minRows={2}
        />

        <LocationPickerMap
          latitude={form.latitude}
          longitude={form.longitude}
          defaultCenter={option?.mapCenter}
          onChange={({ latitude, longitude }) => setForm((prev) => ({ ...prev, latitude, longitude }))}
        />
        <Stack direction="row" spacing={2}>
          <TextField
            fullWidth
            label="Latitude"
            type="number"
            value={form.latitude}
            onChange={setField('latitude')}
          />
          <TextField
            fullWidth
            label="Longitude"
            type="number"
            value={form.longitude}
            onChange={setField('longitude')}
          />
        </Stack>

        <FormControlLabel
          label="Active (shown in Create Order)"
          control={
            <Switch
              checked={form.is_active}
              onChange={(event) => setForm((prev) => ({ ...prev, is_active: event.target.checked }))}
            />
          }
        />
      </Stack>

      <Stack
        direction="row"
        justifyContent="flex-end"
        spacing={1.5}
        sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider' }}
      >
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {area ? 'Save' : 'Create'}
        </Button>
      </Stack>
    </Drawer>
  );
};

DeliveryAreaDrawer.propTypes = {
  open: PropTypes.bool.isRequired,
  area: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func.isRequired,
};

DeliveryAreaDrawer.defaultProps = {
  area: null,
};

export default DeliveryAreaDrawer;
